import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Table,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  Typography,
  TableContainer,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import Label from 'src/components/label/Label';
import Scrollbar from 'src/components/scrollbar';

export default function RoleTable({
  roles,
  activeTab,
  userCapabilities,
  onEditInfo,
  onEditPermissions,
  onSync,
  onArchive,
  onRestore,
}) {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const { canUpdateRole, canDeleteRole, canManagePermissions } = userCapabilities;

  const handleOpenMenu = (event, row) => {
    event.stopPropagation();
    setOpenMenu(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const formatRoleName = (roleName) => {
    if (!roleName) return '';
    const translated = t(`roles.${roleName}`);
    if (translated === `roles.${roleName}` || translated.startsWith('roles.')) {
      return roleName
        .replace('ROLE_', '')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return translated;
  };

  const isActiveView = activeTab === 'active';

  const showOptionsColumn = isActiveView
    ? canUpdateRole || canManagePermissions || canDeleteRole
    : canUpdateRole;

  return (
    <>
      <TableContainer>
        <Scrollbar>
          <Table size="medium">
            <TableHead>
              {/* Dynamic Header Row: Sets clean colors, weights, and backgrounds for dark mode */}
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    color: 'text.secondary',
                    fontWeight: 700,
                    bgcolor: 'background.neutral', // Resolves to #0d1117 in dark mode
                  },
                }}
              >
                <TableCell>{t('rolesPage.roleName')}</TableCell>
                <TableCell>{t('rolesPage.description')}</TableCell>
                {isActiveView && (
                  <TableCell align="center">{t('rolesPage.userCount')}</TableCell>
                )}
                <TableCell>{t('rolesPage.status')}</TableCell>
                {showOptionsColumn && (
                  <TableCell align="right">{t('rolesPage.options')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.disabled',
                        fontStyle: 'italic',
                        fontWeight: 600,
                      }}
                    >
                      {t('tables.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((row) => {
                  const isActive = row.active !== undefined ? row.active : row.isActive;

                  const isClickable =
                    isActiveView && canUpdateRole && canManagePermissions;

                  return (
                    <TableRow
                      key={row.id}
                      hover={isClickable}
                      onClick={() => isClickable && onEditPermissions(row)}
                      sx={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary' }}>
                        {formatRoleName(row.roleName)}
                      </TableCell>

                      {/* Description Cell: Muted secondary text */}
                      <TableCell sx={{ color: 'text.secondary', opacity: 0.85 }}>
                        {row.description}
                      </TableCell>

                      {isActiveView && (
                        <TableCell align="center">
                          <Label variant="soft" color="info">
                            {row.userCount}
                          </Label>
                        </TableCell>
                      )}

                      <TableCell>
                        <Label color={isActive ? 'success' : 'error'}>
                          {isActive ? t('rolesPage.active') : t('rolesPage.archived')}
                        </Label>
                      </TableCell>

                      {showOptionsColumn && (
                        <TableCell align="right">
                          <IconButton onClick={(e) => handleOpenMenu(e, row)}>
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <Popover
        open={Boolean(openMenu)}
        anchorEl={openMenu}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            minWidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {isActiveView ? (
          <>
            {canUpdateRole && canManagePermissions && (
              <MenuItem
                onClick={() => {
                  onEditPermissions(selectedRow);
                  handleCloseMenu();
                }}
              >
                <Iconify icon="eva:shield-fill" sx={{ mr: 2 }} />
                {t('rolesPage.editPermissions')}
              </MenuItem>
            )}

            {canManagePermissions && (
              <MenuItem
                onClick={() => {
                  onSync(selectedRow);
                  handleCloseMenu();
                }}
              >
                <Iconify icon="eva:sync-fill" sx={{ mr: 2 }} />
                {t('rolesPage.syncChanges')}
              </MenuItem>
            )}

            {canDeleteRole && (
              <MenuItem
                onClick={() => {
                  onArchive(selectedRow);
                  handleCloseMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
                {t('rolesPage.deactivate')}
              </MenuItem>
            )}
          </>
        ) : (
          canUpdateRole && (
            <MenuItem
              onClick={() => {
                onRestore(selectedRow);
                handleCloseMenu();
              }}
              sx={{ color: 'success.main' }}
            >
              <Iconify icon="eva:undo-fill" sx={{ mr: 2 }} />
              {t('rolesPage.unarchive')}
            </MenuItem>
          )
        )}
      </Popover>
    </>
  );
}

RoleTable.propTypes = {
  roles: PropTypes.array.isRequired,
  activeTab: PropTypes.string.isRequired,
  userCapabilities: PropTypes.object.isRequired,
  onEditInfo: PropTypes.func.isRequired,
  onEditPermissions: PropTypes.func.isRequired,
  onSync: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
};
